import type { DropdownOption } from '../types';

/** Default fallback color for status options without a color */
export const DEFAULT_STATUS_COLOR = '#9e9e9e';

/** Default fallback color for boards without a color */
export const DEFAULT_BOARD_COLOR = '#1976d2';

/** Column keys rendered as dedicated fields — excluded from the dynamic columns section */
export const EXCLUDED_COLUMN_KEYS = new Set([
  'description',
  'notes',
  'status',
  'priority',
  'assigned_to',
]);

/** Default status options (fallback when board columns are not loaded) */
export const DEFAULT_STATUS_OPTIONS: DropdownOption[] = [
  { id: 'todo', label: 'Todo', color: '#9e9e9e', category: 'not_started' },
  { id: 'in_progress', label: 'In Progress', color: '#2196f3', category: 'active' },
  { id: 'review', label: 'Review', color: '#ff9800', category: 'active' },
  { id: 'done', label: 'Done', color: '#4caf50', category: 'done' },
  { id: 'cancelled', label: 'Cancelled', color: '#f44336', category: 'cancelled' },
];

export const PRIORITY_VALUES = ['low', 'medium', 'high', 'critical'] as const;

/** Default colors for priority badges (fallback when board has no custom priority options) */
export const PRIORITY_COLORS: Record<string, string> = {
  low: '#8bc34a',
  medium: '#2196f3',
  high: '#ff9800',
  critical: '#f44336',
};

/** Default priority options with colors (fallback when board columns are not loaded) */
export const DEFAULT_PRIORITY_OPTIONS: DropdownOption[] = [
  { id: 'low', label: 'Low', color: '#8bc34a' },
  { id: 'medium', label: 'Medium', color: '#2196f3' },
  { id: 'high', label: 'High', color: '#ff9800' },
  { id: 'critical', label: 'Critical', color: '#f44336' },
];

/**
 * Resolve priority color from board column options, falling back to PRIORITY_COLORS constant.
 * Use this instead of accessing PRIORITY_COLORS directly to respect board-level customization.
 */
export function getPriorityColor(priority: string, priorityOptions?: DropdownOption[]): string {
  if (priorityOptions) {
    const opt = priorityOptions.find((o) => o.id === priority);
    if (opt?.color) return opt.color;
  }
  return PRIORITY_COLORS[priority] || '#9e9e9e';
}

/** Extract priority options from board columns, falling back to DEFAULT_PRIORITY_OPTIONS */
export function resolvePriorityOptions(columns?: { column_key: string; settings: { options?: DropdownOption[] } }[]): DropdownOption[] {
  if (columns) {
    const priorityColumn = columns.find((col) => col.column_key === 'priority');
    if (priorityColumn?.settings?.options && priorityColumn.settings.options.length > 0) {
      return priorityColumn.settings.options;
    }
  }
  return DEFAULT_PRIORITY_OPTIONS;
}

/*
 * ---------------------------------------------------------------------------
 * The priority vocabulary rule — stated ONCE, here, and followed everywhere.
 * ---------------------------------------------------------------------------
 *
 * A task's priority is an id out of THIS board's `priority` column
 * `settings.options` (`.claude/rules/rules-tasks.md` § "A board column's options
 * are its vocabulary"). Templates seed different sets: `low|medium|high|critical`
 * on most boards, `p1|p2|p3` on the Leadership "Initiative Portfolio" template.
 * So neither the LABEL nor the ORDER of a priority can come from a hardcoded list,
 * and every surface that offers, labels, filters or ranks one resolves it through
 * `resolvePriorityOptions` above plus the two functions below.
 *
 * The one rule both functions apply:
 *
 *   A board whose option set is entirely the PLATFORM four keeps platform meaning
 *   — translated labels (`common:priority.*`, so a Spanish user does not read the
 *   template's English "Medium") and the fixed severity `critical > high > medium >
 *   low`, which is NOT the order the default template lists them in.
 *
 *   Any other set is the tenant's own vocabulary: the option's own `label` is the
 *   label (there is no translation for `p1`, and rendering `common:priority.p1` at
 *   a user is the defect this replaces), and the board's own ORDER is the severity,
 *   read most-significant-first — the shape `p1|p2|p3` uses, and the only signal a
 *   `DropdownOption` carries, since it has no severity field.
 *
 * A value the board no longer declares still has to render: it falls back to the
 * platform translation when it is a platform id, and to its own humanised id
 * otherwise. Never a raw i18n key.
 */

/** True for the four ids the platform itself defines (and translates). */
function isPlatformPriorityId(id: string): boolean {
  return (PRIORITY_VALUES as readonly string[]).includes(id);
}

/** True when every option in this set is one of the platform four. */
function isPlatformPrioritySet(options: DropdownOption[] | undefined): boolean {
  return !options || options.length === 0 || options.every((o) => isPlatformPriorityId(o.id));
}

/**
 * The display label for a priority id — see the vocabulary rule above.
 *
 * `t` is the caller's `useTranslation` translator; the key is namespaced
 * (`common:priority.*`) so it resolves from any namespace the caller loaded.
 */
export function getPriorityLabel(
  priorityId: string | undefined | null,
  options: DropdownOption[] | undefined,
  t: (key: string) => string,
): string {
  if (!priorityId) return '';
  if (isPlatformPrioritySet(options) && isPlatformPriorityId(priorityId)) {
    return t(`common:priority.${priorityId}`);
  }
  const option = options?.find((o) => o.id === priorityId);
  if (option) return option.label;
  if (isPlatformPriorityId(priorityId)) return t(`common:priority.${priorityId}`);
  return priorityId.replace(/_/g, ' ');
}

/**
 * The option id a control should ACT on, given what the board actually declares —
 * for EITHER select column, status or priority.
 *
 * A create surface seeds the platform default (`todo` / `medium`) before the
 * board's columns have loaded, and react-hook-form captures `defaultValues` once at
 * mount, so the seed outlives the load. A board declaring its own vocabulary never
 * offers that value: the chip then renders the raw id and Create is refused with a
 * 400 the surface has no field to attach it to. Falling back to the board's FIRST
 * option is the same rule `resolve_default_column_option` applies server-side
 * (`.claude/rules/rules-tasks.md` § A board column's options are its vocabulary).
 *
 * Resolve it DURING RENDER at each use site — never by writing the corrected value
 * back through an effect, which commits one frame showing the wrong one.
 * An empty option set means the column constrains nothing: keep the value.
 */
export function resolveDeclaredOption(
  valueId: string | undefined | null,
  options: DropdownOption[],
): string {
  if (valueId && options.some((opt) => opt.id === valueId)) return valueId;
  return options[0]?.id ?? valueId ?? '';
}

/** Platform severity, lowest number = highest priority. Not the template's list order. */
const PLATFORM_PRIORITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

/** Rank beyond any real option — an id this board does not declare sorts last. */
const UNRANKED_PRIORITY = Number.MAX_SAFE_INTEGER;

/**
 * Severity rank for a priority id, lowest number = highest priority — see the
 * vocabulary rule above. Ranks are DISTINCT per option, so "same priority group"
 * (the drag constraint under a priority sort) means one option, not one bucket.
 */
export function getPriorityRank(
  priorityId: string | undefined | null,
  options?: DropdownOption[],
): number {
  if (!priorityId) return UNRANKED_PRIORITY;
  if (!options || isPlatformPrioritySet(options)) {
    return PLATFORM_PRIORITY_RANK[priorityId] ?? UNRANKED_PRIORITY;
  }
  const index = options.findIndex((o) => o.id === priorityId);
  return index === -1 ? UNRANKED_PRIORITY : index;
}

/**
 * The most significant of `selected` under this board's own order, or `undefined`
 * when none of them is an option the board declares.
 */
export function highestPriorityOf(
  selected: string[],
  options?: DropdownOption[],
): string | undefined {
  let best: string | undefined;
  let bestRank = UNRANKED_PRIORITY;
  for (const id of selected) {
    const rank = getPriorityRank(id, options);
    if (rank < bestRank) {
      best = id;
      bestRank = rank;
    }
  }
  return best;
}

/** Colors for status category groupings (not_started, active, done, cancelled) */
export const CATEGORY_COLORS: Record<string, string> = {
  not_started: '#9e9e9e',
  active: '#2196f3',
  done: '#4caf50',
  cancelled: '#f44336',
};

/** Tailwind class overrides to make MultiUserPicker render inline (borderless, compact) */
export const MULTI_USER_PICKER_INLINE_CLASS =
  '[&_button]:!border-0 [&_button]:!shadow-none [&_button]:!bg-transparent [&_button]:hover:!bg-muted/50 [&_button]:!h-8 [&_button]:!min-h-0 [&_button]:!px-3 [&_button]:!py-0';

